import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { WebView, WebViewMessageEvent } from 'react-native-webview'

import { DS } from '../../design-system'
import { events } from '../../models/analytics/analytics-events'
import { useStore } from '../../models/StoreProvider'
import { ArticleSkeleton } from './ArticleSkeleton'
import { articleReaderUrl, readArticleEventProps, useReadFeed } from './feed/readFeed'
import { COMPLETION_THRESHOLD, useReadProgressStore } from './useReadProgressStore'

/**
 * Reports scroll depth back to the app so we can award diamonds/badges at 70%
 * (PP-4819). This works against the corp site's own HTML, so rewards are not
 * blocked on the web team shipping the chrome-free template.
 *
 * Posting is throttled to whole percentage points to keep the bridge quiet.
 */
const SCROLL_TRACKING_SCRIPT = `
(function () {
  var lastReported = 0;
  function report() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - window.innerHeight;
    var fraction = scrollable <= 0 ? 1 : (window.scrollY || doc.scrollTop || 0) / scrollable;
    fraction = Math.max(0, Math.min(1, fraction));
    if (fraction - lastReported < 0.01) { return; }
    lastReported = fraction;
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'scroll', fraction: fraction }));
  }
  window.addEventListener('scroll', report, { passive: true });
  window.addEventListener('load', report);
  report();
})();
true;
`

/**
 * Hides everything on the corp site's blog template except the article header,
 * hero image and body, until the web team ships a chrome-free template.
 * Selectors come from the live Webflow markup, which every post shares:
 *
 * - `.navigation_wrap`, `.footer_wrap`: site nav and footer. The nav is
 *   `position: sticky`, so nothing else reserves space for it.
 * - `.article_share_wrap`: social share buttons.
 * - `.article_cta_wrap`: the web "Play Your First Game" card below the body.
 * - `.article_component-rail`: side rail with the lessons card and the HubSpot
 *   guide download form.
 * - `.related-article_layout`: "You might also like". Its links leave the
 *   article, which `onShouldStartLoadWithRequest` blocks, so they were dead taps.
 *
 * Injected before content loads so the chrome never paints. `<head>` may not
 * exist yet at that point, so the style goes on `<html>`, where it still applies.
 */
const HIDE_CHROME_CSS = `${[
  '.navigation_wrap',
  '.footer_wrap',
  '.article_share_wrap',
  '.article_cta_wrap',
  '.article_component-rail',
  '.related-article_layout'
].join(', ')} { display: none !important; }`

const HIDE_CHROME_SCRIPT = `
(function () {
  var style = document.createElement('style');
  style.textContent = ${JSON.stringify(HIDE_CHROME_CSS)};
  (document.head || document.documentElement).appendChild(style);
})();
`

/**
 * Tells the app the article is readable so the skeleton can drop before
 * `onLoadEnd`, which waits for every image and third-party script (HubSpot,
 * analytics) on the corp site.
 *
 * "Readable" is HTML parsed *and* web fonts loaded: the title is set in a web
 * font the browser hides until it arrives, so dropping on DOMContentLoaded
 * alone showed a page with a blank gap where the title goes. Fonts are capped
 * at FONT_WAIT_MS so one slow font cannot hold the skeleton up.
 */
const FONT_WAIT_MS = 1500

const READY_SCRIPT = `
(function () {
  var sent = false;
  function ready() {
    if (sent) { return; }
    sent = true;
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ready' }));
  }
  function afterFonts() {
    setTimeout(ready, ${FONT_WAIT_MS});
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(ready, ready); } else { ready(); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', afterFonts, { once: true });
  } else {
    afterFonts();
  }
})();
true;
`

/** Scroll depths (in %) reported as `Article Progress`, once each per visit. */
const READ_PROGRESS_MILESTONES = [10, 25, 50, 75, 90, 100]

export const ArticleScreen = () => {
  const router = useRouter()
  const { articleId, articleIndex, filterCategory } = useLocalSearchParams<{
    articleId: string
    /** Row position in the Read list; absent when opened from a deep link. */
    articleIndex?: string
    filterCategory?: string
  }>()
  const { data, isLoading: isFeedLoading } = useReadFeed()
  const {
    analytics: { setEvent }
  } = useStore()
  const webview = useRef<WebView>(null)
  const [isLoading, setIsLoading] = useState(true)

  const recordScroll = useReadProgressStore((state) => state.recordScroll)
  const markOpened = useReadProgressStore((state) => state.markOpened)
  const openedRef = useRef(false)

  // Per-visit reading stats for analytics. The progress store keeps the
  // lifetime max; these reset every time the reader opens the article.
  const visitMaxScrollRef = useRef(0)
  const reportedMilestonesRef = useRef(new Set<number>())
  const openedAtRef = useRef(Date.now())

  const article = useMemo(() => data?.items.find((item) => item.id === articleId), [data, articleId])

  const eventProps = useMemo(() => {
    if (!article) {
      return undefined
    }
    const index = articleIndex === undefined ? NaN : Number(articleIndex)
    return {
      ...readArticleEventProps(article),
      article_index: Number.isInteger(index) ? index : null,
      filter_category: filterCategory ?? null,
      source: articleIndex === undefined ? 'deep_link' : 'read_list'
    }
  }, [article, articleIndex, filterCategory])

  if (article && eventProps && !openedRef.current) {
    openedRef.current = true
    const { maxScroll = 0, completedAt } = useReadProgressStore.getState().byArticleId[article.id] ?? {}
    setEvent(events.read.article_opened, {
      ...eventProps,
      previous_percent_read: Math.round(maxScroll * 100),
      previously_completed: Boolean(completedAt)
    })
    openedAtRef.current = Date.now()
    markOpened(article.id)
  }

  // Leaving the reader by any route (header back, swipe, hardware back)
  // unmounts it, so the close event goes in the cleanup. A ref keeps the
  // cleanup reading the latest props without re-running the effect.
  const eventPropsRef = useRef(eventProps)
  eventPropsRef.current = eventProps
  useEffect(
    () => () => {
      const props = eventPropsRef.current
      if (!props) {
        return
      }
      const percentRead = Math.round(visitMaxScrollRef.current * 100)
      setEvent(events.read.article_closed, {
        ...props,
        percent_read: percentRead,
        total_percent_read: Math.round(useReadProgressStore.getState().progressFor(props.article_id) * 100),
        completed: percentRead >= COMPLETION_THRESHOLD * 100,
        time_spent_seconds: Math.round((Date.now() - openedAtRef.current) / 1000)
      })
    },
    [setEvent]
  )

  const onMessage = useCallback(
    (event: WebViewMessageEvent) => {
      if (!article || !eventProps) {
        return
      }
      try {
        const payload = JSON.parse(event.nativeEvent.data) as { type?: string; fraction?: number }
        if (payload.type === 'ready') {
          setIsLoading(false)
          return
        }
        if (payload.type !== 'scroll' || typeof payload.fraction !== 'number') {
          return
        }

        visitMaxScrollRef.current = Math.max(visitMaxScrollRef.current, payload.fraction)
        // The bridge throttles to whole points, so the page bottom can arrive as
        // 0.995; rounding lets it still count as 100%.
        const percent = Math.round(payload.fraction * 100)
        READ_PROGRESS_MILESTONES.forEach((milestone) => {
          if (percent >= milestone && !reportedMilestonesRef.current.has(milestone)) {
            reportedMilestonesRef.current.add(milestone)
            setEvent(events.read.article_progress, {
              ...eventProps,
              percent: milestone,
              time_spent_seconds: Math.round((Date.now() - openedAtRef.current) / 1000)
            })
          }
        })

        const justCompleted = recordScroll(article.id, payload.fraction)
        if (justCompleted) {
          // Hook the reward up here: this fires exactly once, the first time the
          // reader passes COMPLETION_THRESHOLD.
          setEvent(events.read.article_completed, {
            ...eventProps,
            percent,
            completion_threshold: COMPLETION_THRESHOLD * 100,
            time_spent_seconds: Math.round((Date.now() - openedAtRef.current) / 1000)
          })
        }
      } catch {
        // A malformed bridge message is not worth interrupting reading for.
      }
    },
    [article, eventProps, recordScroll, setEvent]
  )

  if (!article) {
    if (isFeedLoading) {
      return (
        <SafeAreaView style={styles.container} edges={['top']}>
          <View style={styles.header}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton} hitSlop={8}>
              <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
            </TouchableOpacity>
          </View>
          <View style={styles.body}>
            <ArticleSkeleton />
          </View>
        </SafeAreaView>
      )
    }
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
          </TouchableOpacity>
        </View>
        <View style={styles.centered}>
          <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
            That article is no longer available.
          </DS.Text>
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton} hitSlop={8}>
          <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
        </TouchableOpacity>
        <DS.Text variant="Body Small/Medium" numberOfLines={1} style={styles.headerTitle}>
          {article.title}
        </DS.Text>
      </View>

      <View style={styles.body}>
        <WebView
          ref={webview}
          source={{ uri: articleReaderUrl(article) }}
          originWhitelist={['https://*']}
          onMessage={onMessage}
          injectedJavaScriptBeforeContentLoaded={HIDE_CHROME_SCRIPT + READY_SCRIPT}
          injectedJavaScript={SCROLL_TRACKING_SCRIPT}
          // Fallback if the ready message never arrives (e.g. an error page).
          onLoadEnd={() => setIsLoading(false)}
          // Perf settings the About webview is missing — PP-4819 asks us to rule
          // out our own configuration before blaming the site.
          cacheEnabled
          androidLayerType="hardware"
          decelerationRate="normal"
          setSupportMultipleWindows={false}
          allowsInlineMediaPlayback
          // Keep off-site taps out of the reader: anything that is not this
          // article opens in the system browser instead of hijacking the webview.
          onShouldStartLoadWithRequest={(request) => request.url === articleReaderUrl(article)}
          style={styles.webview}
        />

        {isLoading ? (
          <View style={styles.loadingOverlay} pointerEvents="none">
            <ArticleSkeleton />
          </View>
        ) : null}
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DS.spacing[8],
    paddingHorizontal: DS.spacing[12],
    paddingVertical: DS.spacing[8]
  },
  backButton: {
    padding: DS.spacing[4]
  },
  headerTitle: {
    flex: 1
  },
  webview: {
    flex: 1,
    // Brand/Cream is the corp site's page colour (#F6F2E9), so load gaps and
    // overscroll bounce blend with the article instead of flashing white.
    backgroundColor: DS.palette['Brand/Cream']
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  body: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  loadingOverlay: {
    // The skeleton paints the site's cream itself, so a half-rendered page
    // never shows through on either platform.
    ...StyleSheet.absoluteFillObject
  }
})
